/**
 * Rules Documentation Content
 *
 * DOC-02: Complete Rules documentation for non-technical admins
 * Provides step-by-step guides for creating, testing, and managing notification rules.
 */

export const rulesMetadata = {
  id: 'rules-overview',
  title: 'Rules Engine Overview',
  description: 'Learn how to create, configure, and manage notification rules that automate your workflow.',
  category: 'rules',
  roles: ['admin'],
  keywords: [
    'rules', 'automation', 'notifications', 'conditions', 'triggers',
    'how to create rule', 'rule configuration', 'alert rules', 'automatic notifications',
    'when to send', 'rule logic', 'conditions', 'actions'
  ]
};

export const rulesContent = {
  sections: [
    // Section 1: What are Rules?
    {
      heading: 'What are Rules?',
      content: `Rules let you automatically send notifications when something happens in your gym's sales data. Instead of manually checking numbers and sending alerts, rules do this work for you.

Think of a rule like this: "WHEN something happens, THEN send a notification."`,
      example: {
        title: 'Real Example',
        description: 'When a gym has zero closures by 4 PM, automatically send a WhatsApp alert to the team leader. The rule watches the data for you and sends the message the moment the condition is met.'
      },
      tips: [
        'Every rule has two parts: a condition (the "when") and an action (the "then")',
        'Rules check data automatically - you do not need to trigger them',
        'You can have multiple rules running at the same time'
      ]
    },

    // Section 2: Understanding Conditions
    {
      heading: 'Understanding Conditions',
      content: `Conditions are the "when" part of your rule. They define what situation should trigger a notification.

You can combine multiple conditions using two methods:

**ALL conditions (must all match)**
Every condition you add must be true for the rule to trigger. Use this when you need multiple things to be true at once.

Example: "Connect Rate less than 50 AND Total Closures equals 0" - both must be true.

**ANY condition (just one must match)**
Only one of your conditions needs to be true. Use this when any one situation should trigger an alert.

Example: "Dialed Calls less than 10 OR Connected Calls equals 0" - either triggers the rule.`,
      subsections: [
        {
          title: 'Available Fields',
          items: [
            'Dialed Calls - Total number of calls made',
            'Connected Calls - Calls that were answered',
            'Connect Rate (%) - Percentage of dialed calls that connected',
            'Total Closures - Number of sales closed',
            'Total Visited - Number of walk-in visits',
            'Revenue Expected - Projected revenue amount',
            'TL Verified - Whether team leader verified the entry',
            'Manager Verified - Whether manager verified the entry'
          ]
        },
        {
          title: 'Available Operators',
          items: [
            'Equals (=) - Value must match exactly',
            'Not Equals (!=) - Value must be different',
            'Greater Than (>) - Value must be higher',
            'Less Than (<) - Value must be lower',
            'Greater Than or Equals (>=) - Value must be this amount or higher',
            'Less Than or Equals (<=) - Value must be this amount or lower',
            'Is True - For yes/no fields (like verified)',
            'Is False - For yes/no fields that should not be checked'
          ]
        }
      ],
      example: {
        title: 'Condition Example',
        description: '"Connect Rate less than 50" means the rule triggers when a gym\'s connect rate drops below 50%. If the connect rate is 49%, the rule fires. If it is 50% or higher, the rule does not fire.'
      }
    },

    // Section 3: Understanding Actions
    {
      heading: 'Understanding Actions',
      content: `Actions are the "then" part of your rule. They define what happens when your conditions are met.

You can choose one or more notification channels:`,
      subsections: [
        {
          title: 'Email',
          description: 'Sends an email to the configured recipients. Good for detailed summaries and non-urgent information that people can read later.'
        },
        {
          title: 'WhatsApp',
          description: 'Sends a WhatsApp message to configured phone numbers. Good for urgent alerts that need immediate attention.'
        },
        {
          title: 'Both Email and WhatsApp',
          description: 'Sends both an email and a WhatsApp message. Use this for critical situations that need immediate attention AND a written record.'
        }
      ],
      tips: [
        'Choose WhatsApp for urgent alerts that need immediate action',
        'Choose Email for end-of-day summaries or non-urgent updates',
        'Choose Both for critical alerts like zero closures or very low performance'
      ]
    },

    // Section 4: Step-by-Step Creating Your First Rule
    {
      heading: 'Step-by-Step: Creating Your First Rule',
      content: 'Follow these steps to create a rule that automatically sends notifications when your conditions are met.',
      steps: [
        'Go to the Rules page - Click "Rules" in the sidebar navigation',
        'Click the "Create Rule" button - You will see it in the top right corner',
        'Give your rule a name - Choose something clear like "Low Connect Rate Alert" so you remember what it does',
        'Write a description - Explain what this rule does in plain language (e.g., "Alerts team when connect rate drops below target")',
        'Choose a category - Pick the type that best describes your rule: Performance, Reminder, Celebration, Workflow, or Critical',
        'Add your condition - Select a field (like "Connect Rate"), choose an operator (like "Less Than"), and enter a value (like "40")',
        'Add more conditions if needed - Click "Add" to include additional conditions (all conditions must match by default)',
        'Choose your action - Check "Send Email" and/or "Send WhatsApp" based on how urgent the alert should be',
        'Set priority - Enter a number from 1 to 10, where 1 is highest priority (most important rules run first)',
        'Click "Create Rule" - Your rule is now active and watching your data!'
      ],
      tips: [
        'Start simple with one condition, then add more later if needed',
        'Test your rule before relying on it (see Testing Your Rule section)',
        'Use clear, descriptive names so other admins understand what each rule does'
      ]
    },

    // Section 5: Testing Your Rule
    {
      heading: 'Testing Your Rule',
      content: `Before relying on a rule, you should test it to make sure it works as expected. The test feature lets you simulate data and see if your rule would trigger.`,
      steps: [
        'Go to the Rules page and find your rule',
        'Click the expand arrow to see rule details',
        'Click the "Test Rule" button',
        'Enter test data in JSON format (see example below)',
        'Click "Test Rule" to run the test',
        'Check the result: "Would Trigger" (green) or "Would Not Trigger" (gray)'
      ],
      example: {
        title: 'Test Data Example',
        description: `Enter test data like this:

{
  "dialed": 100,
  "connected": 35,
  "connect_rate": 35,
  "total_closure": 0,
  "total_visited": 10,
  "revenue_expected": 0,
  "tl_verified": true,
  "mgr_verified": false
}

This simulates a gym with 35% connect rate and zero closures.`
      },
      tips: [
        '"Would Trigger" means your conditions matched - the rule would send a notification',
        '"Would Not Trigger" means your conditions did not match - adjust your rule if this is unexpected',
        'Test with different values to make sure your rule catches all the situations you want'
      ]
    },

    // Section 6: Managing Rules
    {
      heading: 'Managing Rules',
      content: 'Once you have created rules, you can enable, disable, edit, or delete them as needed.',
      subsections: [
        {
          title: 'Enable or Disable a Rule',
          description: 'Find the rule in the list and click the toggle switch on the left. When the switch is on (blue), the rule is active and watching your data. When off (gray), the rule is paused and will not send any notifications.'
        },
        {
          title: 'Edit a Rule',
          description: 'Click the expand arrow on a rule to see its details. Currently, to modify a rule you need to delete and recreate it. We recommend disabling the old rule first, creating the new one, testing it, then deleting the old rule.'
        },
        {
          title: 'Delete a Rule',
          description: 'Click the expand arrow on a rule, then click the "Delete" button (red trash icon). You will see a confirmation dialog - click OK to permanently delete the rule. This cannot be undone.'
        }
      ],
      tips: [
        'Disable rules instead of deleting them if you might need them again later',
        'Rules marked as "Template" cannot be deleted - they are system defaults',
        'Check the Activity Logs if you need to see when a rule was changed'
      ]
    },

    // Section 7: Pre-Built Rule Templates
    {
      heading: 'Pre-Built Rule Templates',
      content: `To help you get started quickly, we provide pre-built rule templates. These are ready-made rules for common scenarios that you can use as-is or customize.`,
      steps: [
        'Go to the Rules page',
        'Click the "Templates" tab',
        'Browse the available templates',
        'Each template shows its conditions and what it does',
        'Templates are automatically enabled - disable any you do not want'
      ],
      subsections: [
        {
          title: 'Available Templates',
          items: [
            'Low Connect Rate Alert - Triggers when connect rate drops below target',
            'Zero Closures Warning - Alerts when a gym has no closures',
            'Verification Reminder - Reminds team leaders to verify their entries',
            'Performance Celebration - Sends congratulations when targets are exceeded'
          ]
        }
      ],
      tips: [
        'Templates give you a starting point - you can create your own rules with different values',
        'Review templates regularly to ensure they match your current targets',
        'You cannot delete templates, but you can disable them'
      ]
    },

    // Section 8: Common Examples
    {
      heading: 'Common Examples',
      content: 'Here are real-world rules you might want to create for your gym sales tracking.',
      subsections: [
        {
          title: 'Example 1: Alert when connect rate is below 40%',
          description: `This rule helps you catch gyms that are struggling to reach customers.

**Condition:** Connect Rate less than 40
**Action:** Send WhatsApp
**Priority:** 3 (high priority)
**Category:** Performance

When to use: You want early warning when a gym's calling effectiveness drops.`
        },
        {
          title: 'Example 2: Alert when zero closures after P2',
          description: `This rule flags gyms that have not made any sales by mid-afternoon.

**Condition:** Total Closures equals 0
**Action:** Send Both (Email and WhatsApp)
**Priority:** 2 (very high priority)
**Category:** Critical

When to use: Zero closures by afternoon is a red flag that needs immediate attention.`
        },
        {
          title: 'Example 3: Celebrate when closures exceed 10',
          description: `This rule sends positive recognition when a gym exceeds expectations.

**Condition:** Total Closures greater than 10
**Action:** Send Email
**Priority:** 8 (lower priority - not urgent)
**Category:** Celebration

When to use: You want to recognize and motivate high-performing teams.`
        }
      ],
      tips: [
        'Adjust the values (40%, 10 closures, etc.) based on your actual targets',
        'Start with a few critical rules and add more as you learn what is useful',
        'Review rule effectiveness monthly - disable rules that create noise without value'
      ]
    }
  ]
};

export default rulesContent;
